import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Check, X, AlertTriangle, Camera, CameraOff, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_THEME = {
  valid: { Icon: Check, color: '#B5FF00', label: 'VALID' },
  used: { Icon: AlertTriangle, color: '#facc15', label: 'ALREADY SCANNED' },
  invalid: { Icon: X, color: '#ef4444', label: 'INVALID QR' },
  expired: { Icon: RotateCw, color: '#fb923c', label: 'EXPIRED — ASK TO REFRESH' },
};

const ScanPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scannerRef = useRef(null);
  const [scannerReady, setScannerReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [scanCount, setScanCount] = useState({ valid: 0, used: 0, invalid: 0 });
  const cooldownRef = useRef(0); // prevents flooding when camera repeatedly reads the same QR
  const autoSubmittedRef = useRef(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!user.is_admin && !user.is_promoter && !user.is_organizer) {
        toast.error('Promoter or organizer access required');
        navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  // Init scanner once
  useEffect(() => {
    if (authLoading || !user) return;
    if (!user.is_admin && !user.is_promoter && !user.is_organizer) return;
    if (scannerRef.current) return;
    scannerRef.current = new Html5Qrcode('unight-qr-reader', { verbose: false });
    setScannerReady(true);
    return () => {
      const inst = scannerRef.current;
      if (inst) {
        // Guard against html5-qrcode's "Cannot stop, scanner is not running or paused"
        // console error during rapid unmount/remount cycles (React StrictMode etc.).
        try {
          const state = typeof inst.getState === 'function' ? inst.getState() : null;
          // states: NOT_STARTED=1, SCANNING=2, PAUSED=3
          if (state === 2 || state === 3) {
            inst.stop().catch(() => {}).finally(() => inst.clear().catch(() => {}));
          } else {
            inst.clear().catch(() => {});
          }
        } catch (err) {
          // instance may already be torn down — safe to swallow but log in dev
          if (process.env.NODE_ENV !== 'production') console.debug('[scan] teardown swallow:', err?.message || err);
        }
        scannerRef.current = null;
      }
    };
  }, [authLoading, user]);

  // Auto check-in when arriving via a QR-linked URL like /scan?c=GLQ-XXXX
  // (native camera apps redirect users here when they scan a guest QR)
  useEffect(() => {
    if (authLoading || !user || !token) return;
    if (!user.is_admin && !user.is_promoter && !user.is_organizer) return;
    const code = searchParams.get('c');
    if (!code) return;
    if (autoSubmittedRef.current === code) return;
    autoSubmittedRef.current = code;
    submitScan(code);
    // Clear the query so re-focus / navigation doesn't re-submit
    searchParams.delete('c');
    setSearchParams(searchParams, { replace: true });
  }, [authLoading, user, token]);

  const submitScan = async (qrToken) => {
    try {
      const res = await axios.post(
        `${API}/tickets/check-in`,
        { qr_token: qrToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setLastResult(data);
      setScanCount((c) => ({
        ...c,
        [data.status === 'valid' ? 'valid' : data.status === 'used' ? 'used' : 'invalid']:
          (c[data.status === 'valid' ? 'valid' : data.status === 'used' ? 'used' : 'invalid'] || 0) + 1,
      }));
      // Physical-count reminder — active nudge for door staff so they physically
      // count the correct number of people through the door. Especially critical
      // for couples (2 people) after a real-event bug where a group of 5 tried
      // to piggy-back on one QR (iter-20).
      if (data.status === 'valid' && data.ticket) {
        const size = data.ticket.party_size || 1;
        const label = data.ticket.party_label || 'Guest';
        const name = data.ticket.attendee_name || 'Guest';
        const partner = data.ticket.partner_name;
        if (size > 1) {
          toast.success(
            `× ${size} PEOPLE — ${label}\nLet ${name}${partner ? ` + ${partner}` : ''} through, then scan the next QR`,
            { duration: 6000, className: 'text-base font-mono' }
          );
          // Extra haptic cue on supported devices (Android Chrome, some iOS PWAs)
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate([80, 60, 80]);  // double buzz = 2 people
          }
        } else {
          toast.success(`× 1 PERSON — ${label}\nLet ${name} through`, {
            duration: 4000,
            className: 'text-base font-mono',
          });
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(80);            // single buzz = 1 person
          }
        }
      } else if (data.status === 'used') {
        toast.warning(`Already scanned — ${data.ticket?.attendee_name || 'guest'} was let in earlier`);
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([200, 60, 200]);
      } else if (data.status === 'invalid') {
        toast.error(data.message || 'Invalid QR');
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(400);
      }
      // Refresh door stats if we have an event id
      if (data.ticket?.id) refreshStats(data);
    } catch (err) {
      setLastResult({ status: 'invalid', message: err.response?.data?.detail || 'Network error' });
    }
  };

  const refreshStats = async (data) => {
    try {
      // Use the event id from the most recent valid scan
      if (!data?.ticket?.id) return;
      const ticketRes = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const matched = ticketRes.data.find((t) => t.id === data.ticket.id);
      if (matched) {
        const sres = await axios.get(`${API}/events/${matched.event_id}/door-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(sres.data);
      }
    } catch (err) {
      // Door-stats polling is best-effort; keep last known value if it fails.
      if (process.env.NODE_ENV !== 'production') console.warn('door-stats poll failed', err);
    }
  };

  const handleScan = async (decodedText) => {
    const now = Date.now();
    if (now - cooldownRef.current < 1500) return; // debounce duplicate frames
    cooldownRef.current = now;
    await submitScan(decodedText);
  };

  const startCamera = async () => {
    if (!scannerRef.current) return;
    try {
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        handleScan,
        () => {}, // silently ignore "no QR in frame" errors
      );
      setScanning(true);
    } catch (err) {
      toast.error('Unable to start camera. Grant permission and reload.');
    }
  };

  const stopCamera = async () => {
    if (!scannerRef.current) return;
    try {
      await scannerRef.current.stop();
      setScanning(false);
    } catch (err) {
      // Stopping a camera that's already stopped is fine; log for diagnostics.
      if (process.env.NODE_ENV !== 'production') console.warn('camera stop failed', err);
    }
  };

  const theme = lastResult ? STATUS_THEME[lastResult.status] || STATUS_THEME.invalid : null;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="scan-page">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <div className="mb-8">
          <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">DOOR SCANNER</span>
          <h1 className="font-['Josefin_Sans'] text-4xl md:text-6xl font-bold text-white">
            SCAN TICKETS
          </h1>
          <p className="text-zinc-500 text-sm mt-3 font-mono">
            Point camera at attendee QR. Single-use — accepts tickets &amp; guest-list RSVPs.
          </p>
        </div>

        {/* Live stats */}
        {stats && (
          <div className="bg-[#0A0A0A] border border-zinc-800 p-4 mb-6 grid grid-cols-3 gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-widest text-zinc-500">CHECKED IN</p>
              <p className="font-['Josefin_Sans'] text-3xl text-[#B5FF00]">{stats.checked_in}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-widest text-zinc-500">REMAINING</p>
              <p className="font-['Josefin_Sans'] text-3xl text-white">{stats.remaining}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-widest text-zinc-500">TOTAL</p>
              <p className="font-['Josefin_Sans'] text-3xl text-zinc-400">{stats.total_tickets}</p>
            </div>
          </div>
        )}

        {/* Camera viewport */}
        <div className="relative bg-black border border-zinc-800 mb-6 aspect-square max-w-md mx-auto overflow-hidden">
          <div id="unight-qr-reader" className="w-full h-full" />
          {!scanning && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center">
                <Camera size={56} className="text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 font-mono text-xs tracking-widest mb-4">CAMERA OFF</p>
                <button
                  onClick={startCamera}
                  disabled={!scannerReady}
                  className="btn-primary"
                  data-testid="start-camera-btn"
                >
                  START SCANNING
                </button>
              </div>
            </div>
          )}
        </div>

        {scanning && (
          <div className="flex justify-center mb-6">
            <button
              onClick={stopCamera}
              className="font-mono text-xs text-zinc-400 hover:text-white border border-zinc-700 px-4 py-2 flex items-center gap-2"
              data-testid="stop-camera-btn"
            >
              <CameraOff size={14} /> STOP CAMERA
            </button>
          </div>
        )}

        {/* Last scan result — large, colour-coded */}
        {lastResult && theme && (
          <div
            className="border-2 p-6 mb-6 transition-colors"
            style={{ borderColor: theme.color, background: `${theme.color}10` }}
            data-testid="scan-result"
          >
            <div className="flex items-center gap-4 mb-3">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: theme.color }}
              >
                <theme.Icon size={28} color="#050505" strokeWidth={3} />
              </div>
              <div>
                <p className="font-mono text-xs tracking-widest" style={{ color: theme.color }}>
                  {theme.label}
                </p>
                <p className="font-['Josefin_Sans'] text-2xl text-white" data-testid="scan-attendee-name">
                  {lastResult.ticket?.attendee_name || lastResult.message}
                </p>
                {/* Ticket-type badge — CRITICAL: door staff needs this to instantly
                    spot STAG vs COUPLE and reject groups piggy-backing on one QR. */}
                {lastResult.ticket?.party_label && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1 font-mono text-xs tracking-widest border"
                      style={{
                        color: lastResult.ticket.party_type === 'couple' ? '#B5FF00' : '#F4A340',
                        borderColor: lastResult.ticket.party_type === 'couple' ? '#B5FF00' : '#F4A340',
                        background: lastResult.ticket.party_type === 'couple' ? 'rgba(181,255,0,0.08)' : 'rgba(244,163,64,0.08)',
                      }}
                      data-testid="scan-ticket-type-badge"
                    >
                      {lastResult.ticket.party_label}
                    </span>
                    <span
                      className="font-mono text-xs tracking-widest text-zinc-400"
                      data-testid="scan-party-size"
                    >
                      × {lastResult.ticket.party_size || 1} {(lastResult.ticket.party_size || 1) > 1 ? 'PEOPLE' : 'PERSON'}
                    </span>
                  </div>
                )}
                {lastResult.ticket?.party_type === 'couple' && lastResult.ticket?.partner_name && (
                  <p
                    className="mt-2 font-mono text-[11px] text-zinc-400 tracking-wide"
                    data-testid="scan-partner-name"
                  >
                    +1 · <span className="text-white">{lastResult.ticket.partner_name}</span>
                  </p>
                )}
              </div>
            </div>
            {lastResult.ticket && (
              <div className="grid grid-cols-2 gap-3 text-sm pt-3 border-t border-zinc-800/70">
                <div>
                  <p className="font-mono text-[10px] text-zinc-500 tracking-widest">EVENT</p>
                  <p className="text-white">{lastResult.ticket.event_title}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] text-zinc-500 tracking-widest">TIER</p>
                  <p className="text-white">{lastResult.ticket.tier_name}</p>
                </div>
                {lastResult.ticket.seat_number && (
                  <div>
                    <p className="font-mono text-[10px] text-zinc-500 tracking-widest">SEAT</p>
                    <p className="text-white">{lastResult.ticket.seat_number}</p>
                  </div>
                )}
                {lastResult.ticket.used_at && (
                  <div>
                    <p className="font-mono text-[10px] text-zinc-500 tracking-widest">
                      {lastResult.status === 'used' ? 'PREVIOUSLY SCANNED' : 'SCANNED'}
                    </p>
                    <p className="text-white font-mono text-xs">
                      {new Date(lastResult.ticket.used_at).toLocaleTimeString('en-GB')}
                    </p>
                  </div>
                )}
              </div>
            )}
            {lastResult.terms_outdated && (
              <div className="mt-3 border border-amber-500/40 bg-amber-500/5 p-3" data-testid="scan-terms-outdated-warning">
                <p className="font-mono text-xs text-amber-400 tracking-widest mb-1">⚠ TERMS UPDATED</p>
                <p className="text-zinc-300 text-xs">
                  Guest signed off on an older version. Reconfirm verbally before letting them in.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Session counters */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
            <p className="font-['Josefin_Sans'] text-2xl text-[#B5FF00]" data-testid="count-valid">
              {scanCount.valid}
            </p>
            <p className="font-mono text-[10px] tracking-widest text-zinc-500">VALID</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
            <p className="font-['Josefin_Sans'] text-2xl text-yellow-400">{scanCount.used}</p>
            <p className="font-mono text-[10px] tracking-widest text-zinc-500">DUPES</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
            <p className="font-['Josefin_Sans'] text-2xl text-red-400">{scanCount.invalid}</p>
            <p className="font-mono text-[10px] tracking-widest text-zinc-500">REJECTED</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanPage;
